import { Routes } from "@angular/router";
import { authGuard } from "./core/guards/auth.guard";
import { devOnlyGuard } from "./features/api-benchmarking/guards/dev-only.guard";
import { nonShopifyGuard } from "./core/guards/non-shopify.guard";
import { quickActionsFeatureGuard } from "./core/guards/quick-actions-feature.guard";

export interface BreadcrumbData {
  label: string;
  path?: string;
}

export const routes: Routes = [
  {
    path: "widgets/dashboard/:siteId",
    loadComponent: () =>
      import("./features/widgets/dashboard/widgets-dashboard.component").then(
        (m) => m.WidgetsDashboardComponent,
      ),
    title: "Dashboard - QMC",
    canActivate: [authGuard],
    children: [
      {
        path: "",
        loadComponent: () =>
          import(
            "./features/dashboard/components/dashboard-container/dashboard-container.component"
          ).then((m) => m.DashboardContainerComponent),
        title: "Dashboard - QMC",
        data: {
          breadcrumb: { label: "Dashboard", path: "" },
        },
      },
      {
        path: "dashboard",
        loadComponent: () =>
          import(
            "./features/dashboard/components/dashboard-scheduled-orders-snapshot-report.component"
          ).then((m) => m.DashboardScheduledOrdersSnapshotReportComponent),
        title: "Dashboard - QMC",
        data: {
          breadcrumb: { label: "Dashboard", path: "dashboard" },
        },
      },
      {
        path: "scheduled-orders",
        loadComponent: () =>
          import(
            "./features/scheduled-orders/pages/scheduled-orders-list/scheduled-orders-list.component"
          ).then((m) => m.ScheduledOrdersListComponent),
        title: "Scheduled Orders - QMC",
        data: {
          breadcrumb: {
            label: "Scheduled Orders",
            path: "scheduled-orders",
          },
        },
      },
      {
        path: "scheduled-orders/:id/edit",
        loadComponent: () =>
          import(
            "./features/scheduled-orders/pages/edit/edit-scheduled-order.component"
          ).then((m) => m.EditScheduledOrderComponent),
        title: "Edit Scheduled Order - QMC",
        data: {
          breadcrumb: {
            label: "Edit Scheduled Order",
            path: "scheduled-orders/:id/edit",
          },
        },
      },
      {
        path: "customers",
        loadComponent: () =>
          import(
            "./features/customers/pages/customers-list/customers-list.component"
          ).then((m) => m.CustomersListComponent),
        title: "Customers - QMC",
        data: {
          breadcrumb: { label: "Customers", path: "customers" },
        },
      },
      {
        path: "customers/:id/edit",
        loadComponent: () =>
          import(
            "./features/customers/pages/customer-edit/customer-edit.component"
          ).then((m) => m.CustomerEditComponent),
        title: "Edit Customer - QMC",
        data: {
          breadcrumb: { label: "Edit Customer", path: "customers/:id/edit" },
        },
      },
      {
        path: "orders",
        loadComponent: () =>
          import(
            "./features/orders/components/dashboard-orders.component"
          ).then((m) => m.DashboardOrdersComponent),
        title: "Orders - QMC",
        data: {
          breadcrumb: { label: "Orders", path: "orders" },
        },
      },
      {
        path: "orders/create",
        loadComponent: () =>
          import(
            "./features/orders/components/dashboard-orders-create.component"
          ).then((m) => m.DashboardOrdersCreateComponent),
        title: "Create Order - QMC",
        data: {
          breadcrumb: { label: "Create Order", path: "orders/create" },
        },
      },
      {
        path: "orders/:id",
        loadComponent: () =>
          import(
            "./features/orders/components/dashboard-orders-detail.component"
          ).then((m) => m.DashboardOrdersDetailComponent),
        title: "Order Details - QMC",
        data: {
          breadcrumb: { label: "Order Details", path: "orders/:id" },
        },
      },
      {
        path: "orders/:id/edit",
        loadComponent: () =>
          import(
            "./features/orders/components/dashboard-orders-update.component"
          ).then((m) => m.DashboardOrdersUpdateComponent),
        title: "Edit Order - QMC",
        data: {
          breadcrumb: { label: "Edit Order", path: "orders/:id/edit" },
        },
      },
      {
        path: "products",
        loadComponent: () =>
          import(
            "./features/products/components/dashboard-products.component"
          ).then((m) => m.DashboardProductsComponent),
        title: "Products - QMC",
        data: {
          breadcrumb: { label: "Products", path: "products" },
        },
      },
      {
        path: "products/:id",
        loadComponent: () =>
          import(
            "./features/products/components/dashboard-products-detail.component"
          ).then((m) => m.DashboardProductsDetailComponent),
        title: "Product Details - QMC",
        data: {
          breadcrumb: { label: "Product Details", path: "products/:id" },
        },
      },
      {
        path: "reports/products-by-date-summary",
        loadComponent: () =>
          import(
            "./features/reports/components/dashboard-products-by-date-summary/dashboard-products-by-date-summary.component"
          ).then((m) => m.DashboardProductsByDateSummaryComponent),
        title: "Products by Date Summary - QMC",
        data: {
          breadcrumb: {
            label: "Products by Date Summary",
            path: "reports/products-by-date-summary",
          },
        },
      },
      {
        path: "reports/scheduled-orders-by-product",
        loadComponent: () =>
          import(
            "./features/reports/components/dashboard-scheduled-orders-by-product/dashboard-scheduled-orders-by-product.component"
          ).then((m) => m.DashboardScheduledOrdersByProductComponent),
        title: "Scheduled Orders by Product - QMC",
        data: {
          breadcrumb: {
            label: "Scheduled Orders by Product",
            path: "reports/scheduled-orders-by-product",
          },
        },
      },
      {
        path: "reports/event-logs",
        loadComponent: () =>
          import(
            "./features/reports/components/dashboard-event-logs/dashboard-event-logs.component"
          ).then((m) => m.DashboardEventLogsComponent),
        title: "Event Logs - QMC",
        data: {
          breadcrumb: { label: "Event Logs", path: "reports/event-logs" },
        },
      },
      {
        path: "scheduled-orders/new",
        loadComponent: () =>
          import(
            "./features/scheduled-orders/pages/create/create-scheduled-order.component"
          ).then((m) => m.CreateScheduledOrderComponent),
        title: "Create Scheduled Order - QMC",
        canActivate: [nonShopifyGuard],
        data: {
          breadcrumb: {
            label: "Create Scheduled Order",
            path: "scheduled-orders/new",
          },
        },
      },
      {
        path: "scheduled-orders/:id",
        loadComponent: () =>
          import(
            "./features/scheduled-orders/pages/scheduled-orders-list/scheduled-orders-list.component"
          ).then((m) => m.ScheduledOrdersListComponent),
        title: "Scheduled Order Details - QMC",
        data: {
          breadcrumb: {
            label: "Order Details",
            path: "scheduled-orders/:id",
          },
        },
      },
      {
        path: "scheduled-orders/:id/edit/processing-cycles/:processingCycleId",
        loadComponent: () =>
          import(
            "./features/scheduled-orders/components/dashboard-processing-cycle-view/dashboard-processing-cycle-view.component"
          ).then((m) => m.DashboardProcessingCycleViewComponent),
        title: "Processing Cycle - QMC",
        data: {
          breadcrumb: {
            label: "Processing Cycle",
            path: "scheduled-orders/:id/edit/processing-cycles/:processingCycleId",
          },
        },
      },
      {
        path: "quick-actions",
        loadComponent: () =>
          import(
            "./features/quick-links/pages/quick-links-wrapper/quick-links-wrapper.component"
          ).then((m) => m.QuickLinksWrapperComponent),
        title: "Quick Actions - QMC",
        canActivate: [quickActionsFeatureGuard],
        data: {
          breadcrumb: { label: "Quick Actions", path: "quick-actions" },
        },
      },
      {
        path: "quick-actions/create",
        loadComponent: () =>
          import(
            "./features/quick-links/pages/quick-link-form-page/quick-link-form-page.component"
          ).then((m) => m.QuickLinkFormPageComponent),
        title: "Create Quick Action - QMC",
        canActivate: [quickActionsFeatureGuard],
        data: {
          breadcrumb: {
            label: "Create Quick Action",
            path: "quick-actions/create",
          },
        },
      },
      {
        path: "quick-actions/edit/:id",
        loadComponent: () =>
          import(
            "./features/quick-links/pages/quick-link-form-page/quick-link-form-page.component"
          ).then((m) => m.QuickLinkFormPageComponent),
        title: "Edit Quick Action - QMC",
        canActivate: [quickActionsFeatureGuard],
        data: {
          breadcrumb: {
            label: "Edit Quick Action",
            path: "quick-actions/edit/:id",
          },
        },
      },
      {
        path: "maps",
        loadComponent: () =>
          import(
            "./features/maps/components/dashboard-maps/dashboard-maps.component"
          ).then((m) => m.DashboardMapsComponent),
        title: "Maps - QMC",
        data: {
          breadcrumb: { label: "Maps", path: "maps" },
        },
      },
      {
        path: "maps/create",
        loadComponent: () =>
          import(
            "./features/maps/components/dashboard-maps-create-edit/dashboard-maps-create-edit.component"
          ).then((m) => m.DashboardMapsCreateEditComponent),
        title: "Create Map - QMC",
        data: {
          breadcrumb: { label: "Create Map", path: "maps/create" },
        },
      },
      {
        path: "maps/:id/edit",
        loadComponent: () =>
          import(
            "./features/maps/components/dashboard-maps-create-edit/dashboard-maps-create-edit.component"
          ).then((m) => m.DashboardMapsCreateEditComponent),
        title: "Edit Map - QMC",
        data: {
          breadcrumb: { label: "Edit Map", path: "maps/:id/edit" },
        },
      },
      {
        path: "shipping-rates",
        loadComponent: () =>
          import(
            "./features/shipping-rates/components/dashboard-shipping-rates/dashboard-shipping-rates.component"
          ).then((m) => m.DashboardShippingRatesComponent),
        title: "Shipping Rates - QMC",
        data: {
          breadcrumb: { label: "Shipping Rates", path: "shipping-rates" },
        },
      },
      {
        path: "shipping-rates/create",
        loadComponent: () =>
          import(
            "./features/shipping-rates/components/dashboard-shipping-rates-create/dashboard-shipping-rates-create.component"
          ).then((m) => m.DashboardShippingRatesCreateComponent),
        title: "Create Shipping Rate - QMC",
        data: {
          breadcrumb: {
            label: "Create Shipping Rate",
            path: "shipping-rates/create",
          },
        },
      },
      {
        path: "shipping-rates/:id",
        loadComponent: () =>
          import(
            "./features/shipping-rates/components/dashboard-shipping-rates-detail/dashboard-shipping-rates-detail.component"
          ).then((m) => m.DashboardShippingRatesDetailComponent),
        title: "Shipping Rate Details - QMC",
        data: {
          breadcrumb: {
            label: "Shipping Rate Details",
            path: "shipping-rates/:id",
          },
        },
      },
      {
        path: "shipping-rates/:id/edit",
        loadComponent: () =>
          import(
            "./features/shipping-rates/components/dashboard-shipping-rates-edit/dashboard-shipping-rates-edit.component"
          ).then((m) => m.DashboardShippingRatesEditComponent),
        title: "Edit Shipping Rate - QMC",
        data: {
          breadcrumb: {
            label: "Edit Shipping Rate",
            path: "shipping-rates/:id/edit",
          },
        },
      },
      {
        path: "coupons",
        loadComponent: () =>
          import(
            "./features/coupons/pages/coupons-list/coupons-list.component"
          ).then((m) => m.CouponsListComponent),
        title: "Coupons - QMC",
        data: {
          breadcrumb: { label: "Coupons", path: "coupons" },
        },
      },
      {
        path: "coupons/create",
        loadComponent: () =>
          import(
            "./features/coupons/pages/coupon-edit/coupon-edit.component"
          ).then((m) => m.CouponEditComponent),
        title: "Create Coupon - QMC",
        data: {
          breadcrumb: { label: "Create Coupon", path: "coupons/create" },
        },
      },
      {
        path: "coupons/:id/edit",
        loadComponent: () =>
          import(
            "./features/coupons/pages/coupon-edit/coupon-edit.component"
          ).then((m) => m.CouponEditComponent),
        title: "Edit Coupon - QMC",
        data: {
          breadcrumb: { label: "Edit Coupon", path: "coupons/:id/edit" },
        },
      },
      {
        path: "tax-rates",
        loadComponent: () =>
          import(
            "./features/tax-rates/components/dashboard-tax-rates/dashboard-tax-rates.component"
          ).then((m) => m.DashboardTaxRatesComponent),
        title: "Tax Rates - QMC",
        data: {
          breadcrumb: { label: "Tax Rates", path: "tax-rates" },
        },
      },
      {
        path: "tax-rates/create",
        loadComponent: () =>
          import(
            "./features/tax-rates/components/dashboard-tax-rates-create/dashboard-tax-rates-create.component"
          ).then((m) => m.DashboardTaxRatesCreateComponent),
        title: "Create Tax Rate - QMC",
        data: {
          breadcrumb: { label: "Create Tax Rate", path: "tax-rates/create" },
        },
      },
      {
        path: "tax-rates/:id/edit",
        loadComponent: () =>
          import(
            "./features/tax-rates/components/dashboard-tax-rates-edit/dashboard-tax-rates-edit.component"
          ).then((m) => m.DashboardTaxRatesEditComponent),
        title: "Edit Tax Rate - QMC",
        data: {
          breadcrumb: { label: "Edit Tax Rate", path: "tax-rates/:id/edit" },
        },
      },
      {
        path: "payment-integrations",
        loadComponent: () =>
          import(
            "./features/payment-integrations/components/dashboard-payment-integrations/dashboard-payment-integrations.component"
          ).then((m) => m.DashboardPaymentIntegrationsComponent),
        title: "Payment Integrations - QMC",
        data: {
          breadcrumb: {
            label: "Payment Integrations",
            path: "payment-integrations",
          },
        },
      },
      {
        path: "payment-integrations/create",
        loadComponent: () =>
          import(
            "./features/payment-integrations/components/dashboard-payment-integrations-create/dashboard-payment-integrations-create.component"
          ).then((m) => m.DashboardPaymentIntegrationsCreateComponent),
        title: "Create Payment Integration - QMC",
        data: {
          breadcrumb: {
            label: "Create Payment Integration",
            path: "payment-integrations/create",
          },
        },
      },
      {
        path: "payment-integrations/:id/edit",
        loadComponent: () =>
          import(
            "./features/payment-integrations/components/dashboard-payment-integrations-edit/dashboard-payment-integrations-edit.component"
          ).then((m) => m.DashboardPaymentIntegrationsEditComponent),
        title: "Edit Payment Integration - QMC",
        data: {
          breadcrumb: {
            label: "Edit Payment Integration",
            path: "payment-integrations/:id/edit",
          },
        },
      },
      {
        path: "reports/scheduled-orders-subscriber-metrics",
        loadComponent: () =>
          import(
            "./features/reports/components/scheduled-orders-subscriber-metrics/scheduled-orders-subscriber-metrics.component"
          ).then((m) => m.ScheduledOrdersSubscriberMetricsComponent),
        title: "Scheduled Orders Subscriber Metrics - QMC",
        data: {
          breadcrumb: {
            label: "Scheduled Orders Subscriber Metrics",
            path: "reports/scheduled-orders-subscriber-metrics",
          },
        },
      },
      {
        path: "reports/scheduled-orders-metrics",
        loadComponent: () =>
          import(
            "./features/reports/components/scheduled-orders-subscriber-metrics/scheduled-orders-subscriber-metrics.component"
          ).then((m) => m.ScheduledOrdersSubscriberMetricsComponent),
        title: "Scheduled Orders Metrics - QMC",
        data: {
          breadcrumb: {
            label: "Scheduled Orders Metrics",
            path: "reports/scheduled-orders-metrics",
          },
        },
      },
      {
        path: "reports/survey-results",
        loadComponent: () =>
          import(
            "./features/reports/components/survey-results/survey-results.component"
          ).then((m) => m.SurveyResultsComponent),
        title: "Survey Results - QMC",
        data: {
          breadcrumb: {
            label: "Survey Results",
            path: "reports/survey-results",
          },
        },
      },
      {
        path: "reports/churn-reports",
        loadComponent: () =>
          import(
            "./features/reports/components/churn-report/churn-report.component"
          ).then((m) => m.ChurnReportComponent),
        title: "Churn Reports - QMC",
        data: {
          breadcrumb: { label: "Churn Reports", path: "reports/churn-reports" },
        },
      },
      {
        path: "reports/customer-metrics",
        loadComponent: () =>
          import(
            "./features/reports/components/customer-metrics-report/customer-metrics-report.component"
          ).then((m) => m.CustomerMetricsReportComponent),
        title: "Customer Metrics - QMC",
        data: {
          breadcrumb: {
            label: "Customer Metrics",
            path: "reports/customer-metrics",
          },
        },
      },
      {
        path: "reports/email-delivery",
        loadComponent: () =>
          import(
            "./features/reports/components/email-delivery-report/email-delivery-report.component"
          ).then((m) => m.EmailDeliveryReportComponent),
        title: "Email Delivery - QMC",
        data: {
          breadcrumb: {
            label: "Email Delivery",
            path: "reports/email-delivery",
          },
        },
      },
      // Retention Workflows - Native implementation (replaced iframe)
      {
        path: "retain-and-grow/retention",
        loadComponent: () =>
          import(
            "./features/retain-and-grow/pages/retention-workflows-list/retention-workflows-list.component"
          ).then((m) => m.RetentionWorkflowsListComponent),
        title: "Retention - QMC",
        data: {
          breadcrumb: { label: "Retention", path: "retain-and-grow/retention" },
        },
      },
      {
        path: "retain-and-grow/retention/create",
        loadComponent: () =>
          import(
            "./features/retain-and-grow/pages/retention-workflow-create-edit/retention-workflow-create-edit.component"
          ).then((m) => m.RetentionWorkflowCreateEditComponent),
        title: "Create Retention - QMC",
        data: {
          breadcrumb: {
            label: "Create Retention",
            path: "retain-and-grow/retention/create",
          },
        },
      },
      {
        path: "retain-and-grow/retention/:id/edit",
        loadComponent: () =>
          import(
            "./features/retain-and-grow/pages/retention-workflow-create-edit/retention-workflow-create-edit.component"
          ).then((m) => m.RetentionWorkflowCreateEditComponent),
        title: "Edit Retention - QMC",
        data: {
          breadcrumb: {
            label: "Edit Retention",
            path: "retain-and-grow/retention/:id/edit",
          },
        },
      },
      {
        path: "retain-and-grow/retention/:id/view",
        loadComponent: () =>
          import(
            "./features/retain-and-grow/pages/retention-workflow-create-edit/retention-workflow-create-edit.component"
          ).then((m) => m.RetentionWorkflowCreateEditComponent),
        title: "View Retention - QMC",
        data: {
          breadcrumb: {
            label: "View Retention",
            path: "retain-and-grow/retention/:id/view",
          },
        },
      },
      {
        path: "settings/customer-portal",
        loadComponent: () =>
          import(
            "./features/settings/components/customer-portal-settings/customer-portal-settings.component"
          ).then((m) => m.CustomerPortalSettingsComponent),
        title: "Customer Portal Settings - QMC",
        data: {
          breadcrumb: {
            label: "Customer Portal Settings",
            path: "settings/customer-portal",
          },
        },
      },
      {
        path: "settings/site-dunning",
        loadComponent: () =>
          import(
            "./features/settings/components/site-dunning-settings/site-dunning-settings.component"
          ).then((m) => m.SiteDunningSettingsComponent),
        title: "Site Dunning Settings - QMC",
        data: {
          breadcrumb: {
            label: "Site Dunning Settings",
            path: "settings/site-dunning",
          },
        },
      },
      {
        path: "settings/email-templates",
        loadComponent: () =>
          import(
            "./features/settings/components/email-templates/email-templates.component"
          ).then((m) => m.EmailTemplatesComponent),
        title: "Email Templates - QMC",
        data: {
          breadcrumb: {
            label: "Email Templates",
            path: "settings/email-templates",
          },
        },
      },
      {
        path: "settings/email-templates/edit/:templateType",
        loadComponent: () =>
          import(
            "./features/settings/pages/email-template-editor/email-template-editor.component"
          ).then((m) => m.EmailTemplateEditorComponent),
        title: "Edit Email Template - QMC",
        data: {
          breadcrumb: {
            label: "Edit Template",
            path: "settings/email-templates/edit/:templateType",
          },
        },
      },
      {
        path: "webhooks",
        loadComponent: () =>
          import(
            "./features/widgets/dashboard/frames/dashboard-webhooks-frame.component"
          ).then((m) => m.DashboardWebhooksFrameComponent),
        title: "Webhooks - QMC",
        data: {
          breadcrumb: { label: "Webhooks", path: "webhooks" },
        },
      },
      {
        path: "webhooks/create",
        loadComponent: () =>
          import(
            "./features/widgets/dashboard/frames/dashboard-webhooks-create-frame.component"
          ).then((m) => m.DashboardWebhooksCreateFrameComponent),
        title: "Create Webhook - QMC",
        data: {
          breadcrumb: { label: "Create Webhook", path: "webhooks/create" },
        },
      },
      {
        path: "webhooks/:id/edit",
        loadComponent: () =>
          import(
            "./features/widgets/dashboard/frames/dashboard-webhooks-edit-frame.component"
          ).then((m) => m.DashboardWebhooksEditFrameComponent),
        title: "Edit Webhook - QMC",
        data: {
          breadcrumb: { label: "Edit Webhook", path: "webhooks/:id/edit" },
        },
      },
      {
        path: "settings/site",
        loadComponent: () =>
          import(
            "./features/widgets/dashboard/frames/dashboard-site-settings-frame.component"
          ).then((m) => m.DashboardSiteSettingsFrameComponent),
        title: "Site Settings - QMC",
        data: {
          breadcrumb: { label: "Site Settings", path: "settings/site" },
        },
      },
      {
        path: "settings/localization",
        loadComponent: () =>
          import(
            "./features/widgets/dashboard/frames/dashboard-settings-localization-frame.component"
          ).then((m) => m.DashboardSettingsLocalizationFrameComponent),
        title: "Localization Settings - QMC",
        data: {
          breadcrumb: { label: "Localization", path: "settings/localization" },
        },
      },
      {
        path: "shipping-integrations",
        loadComponent: () =>
          import(
            "./features/widgets/dashboard/frames/dashboard-shipping-integrations-frame.component"
          ).then((m) => m.DashboardShippingIntegrationsFrameComponent),
        title: "Shipping Integrations - QMC",
        data: {
          breadcrumb: {
            label: "Shipping Integrations",
            path: "shipping-integrations",
          },
        },
      },
      {
        path: "shipping-integrations/create",
        loadComponent: () =>
          import(
            "./features/widgets/dashboard/frames/dashboard-shipping-integrations-create-frame.component"
          ).then((m) => m.DashboardShippingIntegrationsCreateFrameComponent),
        title: "Create Shipping Integration - QMC",
        data: {
          breadcrumb: {
            label: "Create Shipping Integration",
            path: "shipping-integrations/create",
          },
        },
      },
      {
        path: "shipping-integrations/:id/edit",
        loadComponent: () =>
          import(
            "./features/widgets/dashboard/frames/dashboard-shipping-integrations-edit-frame.component"
          ).then((m) => m.DashboardShippingIntegrationsEditFrameComponent),
        title: "Edit Shipping Integration - QMC",
        data: {
          breadcrumb: {
            label: "Edit Shipping Integration",
            path: "shipping-integrations/:id/edit",
          },
        },
      },
      {
        path: "shipping-addresses",
        loadComponent: () =>
          import(
            "./features/widgets/dashboard/frames/dashboard-shipping-addresses-frame.component"
          ).then((m) => m.DashboardShippingAddressesFrameComponent),
        title: "Shipping Addresses - QMC",
        data: {
          breadcrumb: {
            label: "Shipping Addresses",
            path: "shipping-addresses",
          },
        },
      },
      {
        path: "shipping-addresses/create",
        loadComponent: () =>
          import(
            "./features/widgets/dashboard/frames/dashboard-shipping-addresses-create-frame.component"
          ).then((m) => m.DashboardShippingAddressesCreateFrameComponent),
        title: "Create Shipping Address - QMC",
        data: {
          breadcrumb: {
            label: "Create Shipping Address",
            path: "shipping-addresses/create",
          },
        },
      },
      {
        path: "shipping-addresses/:id/edit",
        loadComponent: () =>
          import(
            "./features/widgets/dashboard/frames/dashboard-shipping-addresses-edit-frame.component"
          ).then((m) => m.DashboardShippingAddressesEditFrameComponent),
        title: "Edit Shipping Address - QMC",
        data: {
          breadcrumb: {
            label: "Edit Shipping Address",
            path: "shipping-addresses/:id/edit",
          },
        },
      },
      // Product Groups - Native implementation
      {
        path: "product-groups",
        loadComponent: () =>
          import(
            "./features/product-groups/pages/product-groups-list/product-groups-list.component"
          ).then((m) => m.ProductGroupsListComponent),
        title: "Product Groups - QMC",
        data: {
          breadcrumb: { label: "Product Groups", path: "product-groups" },
        },
      },
      {
        path: "product-groups/create",
        loadComponent: () =>
          import(
            "./features/product-groups/pages/product-group-edit/product-group-edit.component"
          ).then((m) => m.ProductGroupEditComponent),
        title: "Create Product Group - QMC",
        data: {
          breadcrumb: {
            label: "Create Product Group",
            path: "product-groups/create",
          },
        },
      },
      {
        path: "product-groups/:id/edit",
        loadComponent: () =>
          import(
            "./features/product-groups/pages/product-group-edit/product-group-edit.component"
          ).then((m) => m.ProductGroupEditComponent),
        title: "Edit Product Group - QMC",
        data: {
          breadcrumb: {
            label: "Edit Product Group",
            path: "product-groups/:id/edit",
          },
        },
      },
      {
        path: "bundles",
        loadComponent: () =>
          import(
            "./features/widgets/dashboard/frames/bundles-frame.component"
          ).then((m) => m.BundlesFrameComponent),
        title: "Bundles - QMC",
        data: {
          breadcrumb: { label: "Bundles", path: "bundles" },
        },
      },
      {
        path: "bundles/create-bundle",
        loadComponent: () =>
          import(
            "./features/widgets/dashboard/frames/create-bundle-frame.component"
          ).then((m) => m.CreateBundleFrameComponent),
        title: "Create Bundle - QMC",
        data: {
          breadcrumb: { label: "Create Bundle", path: "bundles/create-bundle" },
        },
      },
      {
        path: "bundles/configure-bundle/:id",
        loadComponent: () =>
          import(
            "./features/widgets/dashboard/frames/configure-bundle-frame.component"
          ).then((m) => m.ConfigureBundleFrameComponent),
        title: "Configure Bundle - QMC",
        data: {
          breadcrumb: {
            label: "Configure Bundle",
            path: "bundles/configure-bundle/:id",
          },
        },
      },
    ],
  },
  {
    path: "widgets/scheduled-orders/list",
    loadComponent: () =>
      import(
        "./features/scheduled-orders/pages/scheduled-orders-list/scheduled-orders-list-standalone.component"
      ).then((m) => m.ScheduledOrdersListStandaloneComponent),
    title: "Scheduled Orders List - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/scheduled-orders/create",
    loadComponent: () =>
      import(
        "./features/scheduled-orders/pages/create/create-scheduled-order-standalone.component"
      ).then((m) => m.CreateScheduledOrderStandaloneComponent),
    title: "Create Scheduled Order - QMC",
    canActivate: [authGuard, nonShopifyGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/scheduled-orders/edit",
    loadComponent: () =>
      import(
        "./features/scheduled-orders/pages/edit/edit-scheduled-order-standalone.component"
      ).then((m) => m.EditScheduledOrderStandaloneComponent),
    title: "Edit Scheduled Order - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/processing-cycles/view",
    loadComponent: () =>
      import(
        "./features/scheduled-orders/components/dashboard-processing-cycle-view/dashboard-processing-cycle-view-standalone.component"
      ).then((m) => m.DashboardProcessingCycleViewStandaloneComponent),
    title: "Processing Cycle View - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/customers/list",
    loadComponent: () =>
      import(
        "./features/customers/pages/customers-list/customers-list-standalone.component"
      ).then((m) => m.CustomersListStandaloneComponent),
    title: "Customers List - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/customers/edit",
    loadComponent: () =>
      import(
        "./features/customers/pages/customer-edit/customer-edit-standalone.component"
      ).then((m) => m.CustomerEditStandaloneComponent),
    title: "Edit Customer - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/scheduled-orders/:customerId",
    loadComponent: () =>
      import(
        "./features/widgets/scheduled-orders/scheduled-orders.component"
      ).then((m) => m.ScheduledOrdersComponent),
    title: "Scheduled Orders - QMC",
    canActivate: [authGuard],
  },
  {
    path: "widgets/scheduled-orders/:siteId/:customerExternalId",
    loadComponent: () =>
      import(
        "./features/widgets/scheduled-orders/scheduled-orders.component"
      ).then((m) => m.ScheduledOrdersComponent),
    title: "Scheduled Orders - QMC",
    canActivate: [authGuard],
  },
  {
    path: "widgets/select-frequency/:frequencyType/:frequency",
    loadComponent: () =>
      import(
        "./features/widgets/select-frequency/select-frequency.component"
      ).then((m) => m.SelectFrequencyComponent),
    title: "Select Frequency - QMC",
    canActivate: [authGuard],
  },
  {
    path: "widgets/select-frequency",
    loadComponent: () =>
      import(
        "./features/widgets/select-frequency/select-frequency.component"
      ).then((m) => m.SelectFrequencyComponent),
    title: "Select Frequency - QMC",
    canActivate: [authGuard],
  },
  {
    path: "widgets/settings/customer-portal",
    loadComponent: () =>
      import(
        "./features/settings/components/customer-portal-settings/customer-portal-settings-standalone.component"
      ).then((m) => m.CustomerPortalSettingsStandaloneComponent),
    title: "Customer Portal Settings - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/settings/site-dunning",
    loadComponent: () =>
      import(
        "./features/settings/components/site-dunning-settings/site-dunning-settings-standalone.component"
      ).then((m) => m.SiteDunningSettingsStandaloneComponent),
    title: "Site Dunning Settings - QMC",
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/retention-workflows/list",
    loadComponent: () =>
      import(
        "./features/retain-and-grow/pages/retention-workflows-list/retention-workflows-list-standalone.component"
      ).then((m) => m.RetentionWorkflowsListStandaloneComponent),
    title: "Retention Workflows List - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/retention-workflows/create",
    loadComponent: () =>
      import(
        "./features/retain-and-grow/pages/retention-workflow-create-edit/retention-workflow-create-edit-standalone.component"
      ).then((m) => m.RetentionWorkflowCreateEditStandaloneComponent),
    title: "Create Retention Workflow - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/retention-workflows/edit",
    loadComponent: () =>
      import(
        "./features/retain-and-grow/pages/retention-workflow-create-edit/retention-workflow-create-edit-standalone.component"
      ).then((m) => m.RetentionWorkflowCreateEditStandaloneComponent),
    title: "Edit Retention Workflow - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/retention-workflows/view",
    loadComponent: () =>
      import(
        "./features/retain-and-grow/pages/retention-workflow-create-edit/retention-workflow-create-edit-standalone.component"
      ).then((m) => m.RetentionWorkflowCreateEditStandaloneComponent),
    title: "View Retention Workflow - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  // Coupons standalone routes (iframe mode)
  {
    path: "widgets/coupons/list",
    loadComponent: () =>
      import(
        "./features/coupons/pages/coupons-list/coupons-list-standalone.component"
      ).then((m) => m.CouponsListStandaloneComponent),
    title: "Coupons List - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/coupons/create",
    loadComponent: () =>
      import(
        "./features/coupons/pages/coupon-edit/coupon-edit-standalone.component"
      ).then((m) => m.CouponEditStandaloneComponent),
    title: "Create Coupon - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/coupons/edit",
    loadComponent: () =>
      import(
        "./features/coupons/pages/coupon-edit/coupon-edit-standalone.component"
      ).then((m) => m.CouponEditStandaloneComponent),
    title: "Edit Coupon - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  // Products standalone routes (iframe mode)
  {
    path: "widgets/products/list",
    loadComponent: () =>
      import(
        "./features/products/pages/products-list/products-list-standalone.component"
      ).then((m) => m.ProductsListStandaloneComponent),
    title: "Products List - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/products/view",
    loadComponent: () =>
      import(
        "./features/products/pages/product-detail/product-detail-standalone.component"
      ).then((m) => m.ProductDetailStandaloneComponent),
    title: "Product Details - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  // Quick Links standalone routes (iframe mode)
  {
    path: "widgets/quick-links/list",
    loadComponent: () =>
      import(
        "./features/quick-links/pages/quick-links-wrapper/quick-links-wrapper-standalone.component"
      ).then((m) => m.QuickLinksWrapperStandaloneComponent),
    title: "Quick Links List - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/quick-links/create",
    loadComponent: () =>
      import(
        "./features/quick-links/pages/quick-link-form-page/quick-link-form-page-standalone.component"
      ).then((m) => m.QuickLinkFormPageStandaloneComponent),
    title: "Create Quick Link - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/quick-links/edit",
    loadComponent: () =>
      import(
        "./features/quick-links/pages/quick-link-form-page/quick-link-form-page-standalone.component"
      ).then((m) => m.QuickLinkFormPageStandaloneComponent),
    title: "Edit Quick Link - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  // Product Groups standalone routes (iframe mode)
  {
    path: "widgets/product-groups/list",
    loadComponent: () =>
      import(
        "./features/product-groups/pages/product-groups-list/product-groups-list-standalone.component"
      ).then((m) => m.ProductGroupsListStandaloneComponent),
    title: "Product Groups List - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/product-groups/create",
    loadComponent: () =>
      import(
        "./features/product-groups/pages/product-group-edit/product-group-edit-standalone.component"
      ).then((m) => m.ProductGroupEditStandaloneComponent),
    title: "Create Product Group - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/product-groups/edit",
    loadComponent: () =>
      import(
        "./features/product-groups/pages/product-group-edit/product-group-edit-standalone.component"
      ).then((m) => m.ProductGroupEditStandaloneComponent),
    title: "Edit Product Group - QMC",
    canActivate: [authGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "widgets/settings/email-templates",
    loadComponent: () =>
      import(
        "./features/settings/components/email-templates/email-templates.component"
      ).then((m) => m.EmailTemplatesComponent),
    title: "Email Templates - QMC",
    canActivate: [authGuard],
  },
  {
    path: "widgets/settings/email-templates/edit/:templateType",
    loadComponent: () =>
      import(
        "./features/settings/pages/email-template-editor/email-template-editor.component"
      ).then((m) => m.EmailTemplateEditorComponent),
    title: "Edit Email Template - QMC",
    canActivate: [authGuard],
  },
  {
    path: "widgets/admin-dashboard/administration-dashboard-site-summary",
    loadComponent: () =>
      import(
        "./features/widgets/admin-dashboard/admin-dashboard.component"
      ).then((m) => m.AdminDashboardComponent),
    title: "Admin Dashboard - QMC",
    canActivate: [authGuard],
  },
  {
    path: "widgets/admin-dashboard/administration-edit-entity",
    loadComponent: () =>
      import(
        "./features/widgets/administration-edit-entity/administration-edit-entity.component"
      ).then((m) => m.AdministrationEditEntityComponent),
    title: "Administration Edit Entity - QMC",
    canActivate: [authGuard],
  },
  {
    path: "login",
    loadComponent: () =>
      import("./features/auth/login/login.component").then(
        (m) => m.LoginComponent,
      ),
    title: "Login - QMC",
  },
  {
    path: "sites",
    loadComponent: () =>
      import("./features/sites/sites.component").then((m) => m.SitesComponent),
    canActivate: [authGuard],
  },
  {
    path: "forgot-password",
    loadComponent: () =>
      import("./features/auth/forgot-password/forgot-password.component").then(
        (m) => m.ForgotPasswordComponent,
      ),
    title: "Forgot Password - QMC",
  },
  {
    path: "register",
    loadComponent: () =>
      import("./features/auth/register/register.component").then(
        (m) => m.RegisterComponent,
      ),
    title: "Register - QMC",
  },
  {
    path: "unauthorized",
    loadComponent: () =>
      import("./features/auth/unauthorized/unauthorized.component").then(
        (m) => m.UnauthorizedComponent,
      ),
    title: "Authentication Required - QMC",
  },
  {
    path: "my-account/apps",
    loadComponent: () =>
      import("./features/apps/pages/apps-page/apps-page.component").then(
        (m) => m.AppsPageComponent,
      ),
    title: "Apps & Integrations - QMC",
    canActivate: [authGuard],
  },
  {
    path: "my-account/apps/:id",
    loadComponent: () =>
      import("./features/apps/pages/connect/connect.component").then(
        (m) => m.ConnectComponent,
      ),
    title: "Connect App - QMC",
    canActivate: [authGuard],
  },
  {
    path: "my-account/profile",
    loadComponent: () =>
      import("./features/account/pages/profile/profile-page.component").then(
        (m) => m.ProfilePageComponent,
      ),
    title: "My Profile - QMC",
    canActivate: [authGuard],
  },
  {
    path: "my-account/subscription",
    loadComponent: () =>
      import(
        "./features/account/pages/subscription-management/subscription-management.component"
      ).then((m) => m.SubscriptionManagementComponent),
    title: "Subscription Management - QMC",
    canActivate: [authGuard],
  },
  {
    path: "my-account/subscription/change-plan",
    loadComponent: () =>
      import("./features/account/pages/change-plan/change-plan.component").then(
        (m) => m.ChangePlanComponent,
      ),
    title: "Change Subscription Plan - QMC",
    canActivate: [authGuard],
  },
  {
    path: "my-account/thank-you",
    loadComponent: () =>
      import("./features/account/pages/thank-you/thank-you.component").then(
        (m) => m.ThankYouComponent,
      ),
    title: "Thank You - QMC",
    canActivate: [authGuard],
  },
  {
    path: "dev/api-benchmark",
    loadComponent: () =>
      import(
        "./features/api-benchmarking/components/api-benchmark.component"
      ).then((m) => m.ApiBenchmarkComponent),
    title: "API Benchmarking (DEV) - QMC",
    canActivate: [authGuard, devOnlyGuard],
    data: { isStandalonePage: true },
  },
  {
    path: "access-denied",
    loadComponent: () =>
      import("./shared/components/access-denied/access-denied.component").then(
        (m) => m.AccessDeniedComponent,
      ),
    title: "Access Denied - QMC",
    data: { isStandalonePage: true },
  },
  {
    path: "",
    redirectTo: "login",
    pathMatch: "full",
  },
  {
    path: "**",
    loadComponent: () =>
      import("./shared/components/not-found/not-found.component").then(
        (m) => m.NotFoundComponent,
      ),
    title: "Page Not Found - QMC",
    data: { isStandalonePage: true },
  },
];
